import { Box, Divider, Typography, useTheme, Pagination, Stack } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import React, { useEffect, useState, useRef } from 'react';
import ReactLoading from 'react-loading';
import { useSearchParams } from 'react-router-dom';
import { Schema } from '../../../../amplify/data/resource';
import EmptyStateText from '../../../components/custom/EmptyStateText';
import { useAuth } from '../../../utils/context/useAuthContext';
import EmployeesList from './EmployeesList/EmployeesList';

// @ts-ignore
type Employee = Schema['Employee']['type'];

const apiClient = generateClient<Schema>();

// Storage keys for state preservation
const EMPLOYEES_STATE_KEY = 'employees_page_state';

interface EmployeesPageState {
  currentPage: number;
  scrollPosition: number;
  fileId: string;
}

const Employees: React.FC = () => {
  console.log('[Employees] Component rendering or mounting.');

  const theme = useTheme();
  console.log(
    '[Employees] Theme object obtained:',
    theme ? 'Available' : 'Not Available',
  );

  const { user } = useAuth();
  console.log('[Employees] User object from useAuth context:', user);

  const [searchParams] = useSearchParams();
  const fileIdParam = searchParams.get('fileId');
  console.log('[Employees] fileId parameter from URL:', fileIdParam);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const limit = 10;
  const containerRef = useRef<HTMLDivElement>(null);

  console.log(
    '[Employees] Initialized employees state. Count:',
    employees.length,
  );

  // Restore state on component mount
  useEffect(() => {
    if (fileIdParam) {
      const savedState = sessionStorage.getItem(EMPLOYEES_STATE_KEY);
      if (savedState) {
        try {
          const parsedState: EmployeesPageState = JSON.parse(savedState);
          if (parsedState.fileId === fileIdParam) {
            setCurrentPage(parsedState.currentPage);
            // Restore scroll position after a short delay to ensure content is rendered
            setTimeout(() => {
              if (containerRef.current) {
                containerRef.current.scrollTop = parsedState.scrollPosition;
              }
            }, 200);
          }
        } catch (error) {
          console.error('Error restoring employees page state:', error);
        }
      }
    }
  }, [fileIdParam]);

  // Additional effect to restore scroll position after employees are loaded
  useEffect(() => {
    if (!loading && employees.length > 0 && fileIdParam) {
      const savedState = sessionStorage.getItem(EMPLOYEES_STATE_KEY);
      if (savedState) {
        try {
          const parsedState: EmployeesPageState = JSON.parse(savedState);
          if (parsedState.fileId === fileIdParam && containerRef.current) {
            setTimeout(() => {
              if (containerRef.current) {
                containerRef.current.scrollTop = parsedState.scrollPosition;
              }
            }, 100);
          }
        } catch (error) {
          console.error('Error restoring scroll position:', error);
        }
      }
    }
  }, [loading, employees.length, fileIdParam, currentPage]);

  // Save state before component unmounts
  useEffect(() => {
    const saveState = () => {
      if (fileIdParam && containerRef.current) {
        const state: EmployeesPageState = {
          currentPage,
          scrollPosition: containerRef.current.scrollTop,
          fileId: fileIdParam,
        };
        sessionStorage.setItem(EMPLOYEES_STATE_KEY, JSON.stringify(state));
      }
    };

    const handleBeforeUnload = () => {
      saveState();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveState();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      saveState();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentPage, fileIdParam]);

  // Clear state when fileId changes or component unmounts
  useEffect(() => {
    return () => {
      // Clear state if navigating away from this fileId
      const currentState = sessionStorage.getItem(EMPLOYEES_STATE_KEY);
      if (currentState) {
        try {
          const parsedState: EmployeesPageState = JSON.parse(currentState);
          if (parsedState.fileId !== fileIdParam) {
            sessionStorage.removeItem(EMPLOYEES_STATE_KEY);
          }
        } catch (error) {
          sessionStorage.removeItem(EMPLOYEES_STATE_KEY);
        }
      }
    };
  }, [fileIdParam]);

  useEffect(() => {
    console.log(
      '[Employees useEffect] Effect triggered. Current fileId:',
      fileIdParam,
    );

    const fetchEmployees = async () => {
      console.log(
        '[Employees useEffect fetchEmployees] Starting one-time fetch for employees.',
      );
      if (!fileIdParam) {
        console.log(
          '[Employees useEffect fetchEmployees] No fileId present. Aborting fetch.',
        );
        setLoading(false);
        console.log(
          '[Employees useEffect fetchEmployees] Set loading state to false as no fileId is available.',
        );
        return;
      }

      console.log(
        '[Employees useEffect fetchEmployees] Setting loading state to true for fileId:',
        fileIdParam,
      );
      setLoading(true);
      try {
        console.log(
          '[Employees useEffect fetchEmployees] Attempting to list employees with filter for fileId:',
          fileIdParam,
        );
        const { data: fetchedEmployeesData, errors } =
          await apiClient.models.Employee.listEmployeeByFileId({
            fileId: fileIdParam,
          }, {
            filter: {
              or: [
                {status: {
                  eq: 'IN_PROGRESS'
                }},
                {status: {
                  eq: 'RECORD_CAPTURED_SUCCESS'
                }}
              ]
            }
          });

        if (errors && errors.length > 0) {
          console.error(
            '[Employees useEffect fetchEmployees] Errors received while fetching employees:',
            JSON.stringify(errors),
          );
        }

        const currentEmployees = fetchedEmployeesData || [];
        console.log(
          '[Employees useEffect fetchEmployees] Successfully fetched employees. Count:',
          currentEmployees.length,
          'Data:',
          JSON.stringify(currentEmployees),
        );

        console.log(
          '[Employees useEffect fetchEmployees] Updating employees state.',
        );
        setEmployees([...currentEmployees]);
        setTotalPages(Math.ceil(currentEmployees.length / limit));

        console.log(
          '[Employees useEffect fetchEmployees] Setting loading state to false.',
        );
        setLoading(false);
      } catch (error) {
        console.error(
          '[Employees useEffect fetchEmployees catch] Exception during fetching employees:',
          error,
        );
        console.log(
          '[Employees useEffect fetchEmployees catch] Setting loading state to false due to exception.',
        );
        setLoading(false);
      }
    };

    fetchEmployees();
    console.log(
      '[Employees useEffect] fetchEmployees function has been invoked.',
    );
  }, [fileIdParam]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top when page changes
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    }
  };

  const handleOpenUploadFile = () => {
    console.log('[Employees handleOpenUploadFile] Function invoked.');
  };

  console.log(
    '[Employees] Preparing to render. Loading state:',
    loading,
    'Employees count:',
    employees.length,
  );

  // Get paginated employees
  const paginatedEmployees = employees.slice(
    (currentPage - 1) * limit,
    currentPage * limit
  );

  let contentToRender;
  if (loading) {
    console.log(
      '[Employees render] Conditional: loading is true. Rendering ReactLoading component.',
    );
    contentToRender = (
      <Box sx={{ mt: '100px', display: 'flex', justifyContent: 'center' }}>
        <ReactLoading
          type={'bubbles'}
          color={theme.palette.green.main}
          height={250}
          width={250}
        />
      </Box>
    );
  } else if (employees?.length) {
    console.log(
      '[Employees render] Conditional: loading is false and employees array is not empty. Rendering EmployeesList. Count:',
      employees.length,
    );
    contentToRender = (
      <>
        <EmployeesList employees={paginatedEmployees} currentPage={currentPage} />
        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              margin: '20px 0',
            }}
          >
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                shape="rounded"
              />
            </Stack>
          </Box>
        )}
      </>
    );
  } else {
    console.log(
      '[Employees render] Conditional: loading is false and employees array is empty. Rendering EmptyStateText.',
    );
    contentToRender = (
      <EmptyStateText text="מעקב השיוכים ריק - אין היזונים חוזרים להצגה" />
    );
  }

  console.log('[Employees] Returning JSX.');
  return (
    <Box
      ref={containerRef}
      data-employees-container
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        gap: '20px',
        maxHeight: '100vh',
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <img
            alt="logo"
            src={'/assets/header/file.svg'}
            onClick={handleOpenUploadFile}
            style={{ cursor: 'pointer' }}
          />
        </Box>
        <Typography
          fontSize="30px"
          fontWeight="700"
          fontFamily={'AlmoniDL'}
          textAlign={'right'}
          sx={{ whiteSpace: 'nowrap' }}
        >
          מעקב שיוכים
        </Typography>
      </Box>
      <Divider
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: theme.palette.dark.border,
        }}
      />
      {contentToRender}
    </Box>
  );
};

export default Employees;
